package co.uk.sacursoftware.ssinventory.data.model

import kotlinx.serialization.Serializable
import kotlinx.serialization.SerialName

@Serializable
data class Supplier(
    val id: String,
    val name: String,
    val howToOrder: String? = null,
    val orderAddress: String? = null,
    val thresholdOrderPercentage: Int? = 0
)

@Serializable
data class Item(
    val id: String,
    @SerialName("supplier_id") val supplierId: String,
    val name: String,
    val packDescription: String,
    val unitMeasureType: String,
    val packsPerBox: Double,
    val measurePerPack: Double,
    val weeklyUsageRate: Double,
    val price: Double
)

fun formatWhole(value: Double): String = formatDecimal(value)

fun formatDecimal(value: Double): String {
    val rounded = kotlin.math.round(value * 100.0) / 100.0
    val hundredths = kotlin.math.round(kotlin.math.abs(rounded) * 100.0).toLong()
    val whole = hundredths / 100
    val frac = (hundredths % 100).toInt()
    val sign = if (rounded < 0) "-" else ""
    return when {
        frac == 0 -> "$sign$whole"
        frac % 10 == 0 -> "$sign$whole.${frac / 10}"
        else -> "$sign$whole.${frac.toString().padStart(2, '0')}"
    }
}

fun formatStockAmount(item: Item?, realAmount: Double): String {
    val unit = item?.unitMeasureType?.lowercase()?.trim()
    if (item != null && (unit == "kgs" || unit == "kgp")) {
        val measure = if (item.measurePerPack > 0) item.measurePerPack else 1.0
        val packs = (realAmount / measure).toInt()
        val remainderKg = kotlin.math.round((realAmount - (packs * measure)) * 100.0) / 100.0
        return when {
            packs > 0 && remainderKg > 0.0 -> "$packs packs and ${formatDecimal(remainderKg)} Kg"
            packs > 0 -> "$packs packs"
            remainderKg > 0.0 -> "${formatDecimal(remainderKg)} Kg"
            else -> "0"
        }
    }
    if (item != null && unit == "wp") {
        val packsPerBox = if (item.packsPerBox > 0) item.packsPerBox else 1.0
        val boxes = (realAmount / packsPerBox).toInt()
        val individuals = kotlin.math.round((realAmount - (boxes * packsPerBox)) * 100.0) / 100.0
        val boxLabel = if (boxes == 1) "1 box" else "$boxes boxes"
        val indLabel = "${formatDecimal(individuals)} ind"
        return when {
            boxes > 0 && individuals > 0.0 -> "$boxLabel and $indLabel"
            boxes > 0 -> boxLabel
            individuals > 0.0 -> indLabel
            else -> "0"
        }
    }
    return formatDecimal(realAmount)
}

@Serializable
data class StockCount(
    val id: String,
    @SerialName("supplier_id") val supplierId: String,
    val timestamp: String,
    val lastAmended: String? = null,
    val supplierName: String? = null
)

@Serializable
data class StockCountItem(
    @SerialName("item_id") val itemId: String,
    val countedBoxes: Double? = null,
    val countedIndividuals: Double? = null,
    val countedPercentage: Double? = null,
    val countedRealAmount: Double,
    val name: String? = null
)

@Serializable
data class Order(
    val id: String,
    @SerialName("supplier_id") val supplierId: String,
    val daysCalculated: Int,
    val status: String,
    val createdAt: String,
    val receivedAt: String? = null,
    @SerialName("receiveImageUrl") val receiveImageUrl: String? = null,
    @SerialName("isActionMissing") val isActionMissing: Int? = 0,
    val images: List<String>? = emptyList(),
    val supplierName: String? = null
)

@Serializable
data class AppImage(
    val id: String,
    @SerialName("foreign_id") val foreignId: String,
    @SerialName("image_url") val imageUrl: String,
    @SerialName("createdAt") val createdAt: String? = null
)

@Serializable
data class DailyRecord(
    val date: String,
    @SerialName("cleaningId") val cleaningId: String,
    @SerialName("frontOfHouseId") val frontOfHouseId: String,
    @SerialName("issuesId") val issuesId: String,
    @SerialName("miscellaneousId") val miscellaneousId: String,
    @SerialName("cleaning_images") val cleaningImages: List<AppImage> = emptyList(),
    @SerialName("front_of_house_images") val frontOfHouseImages: List<AppImage> = emptyList(),
    @SerialName("issues_images") val issuesImages: List<AppImage> = emptyList(),
    @SerialName("miscellaneous_images") val miscellaneousImages: List<AppImage> = emptyList()
)

@Serializable
data class SaveDailyImagesRequest(
    val action: String = "save_daily_images",
    val date: String,
    val area: String,
    val images: List<String>
)

@Serializable
data class DailyRoutine(
    val id: String,
    val title: String,
    val notes: String? = null,
    @SerialName("repeat_type") val repeatType: String,
    @SerialName("day_of_week") val dayOfWeek: String? = null,
    val date: String? = null,
    @SerialName("is_ticked") val isTicked: Int = 0,
    @SerialName("created_at") val createdAt: String? = null
)

@Serializable
data class SaveDailyRoutineRequest(
    val action: String = "save_daily_routine",
    val id: String? = null,
    val title: String,
    val notes: String? = null,
    @SerialName("repeat_type") val repeatType: String,
    @SerialName("day_of_week") val dayOfWeek: String? = null,
    val date: String? = null
)

@Serializable
data class DeleteDailyRoutineRequest(
    val action: String = "delete_daily_routine",
    val id: String
)

@Serializable
data class ToggleDailyRoutineRequest(
    val action: String = "toggle_daily_routine",
    val id: String,
    val date: String
)

@Serializable
data class PrepItem(
    val id: String,
    @SerialName("item_name") val itemName: String,
    @SerialName("shelf_life") val shelfLife: String? = null,
    @SerialName("prep_time_hour") val prepTimeHour: String? = null,
    val notes: String? = null
)

@Serializable
data class PrepRoutine(
    val id: String,
    @SerialName("day_of_week") val dayOfWeek: String,
    @SerialName("prep_item_id") val prepItemId: String,
    @SerialName("prep_quantity") val prepQuantity: String? = null,
    val notes: String? = null,
    @SerialName("item_name") val itemName: String? = null
)

@Serializable
data class PlannedPrepItem(
    val id: String,
    val date: String,
    @SerialName("prep_item_id") val prepItemId: String,
    val quantity: String,
    @SerialName("is_ticked") val isTicked: Int = 0,
    @SerialName("createdAt") val createdAt: String? = null,
    @SerialName("item_name") val itemName: String? = null,
    val notes: String? = null
)

@Serializable
data class SavePrepItemRequest(
    val action: String = "save_master_prep_item",
    val id: String? = null,
    @SerialName("item_name") val itemName: String,
    @SerialName("shelf_life") val shelfLife: String? = null,
    @SerialName("prep_time_hour") val prepTimeHour: String? = null,
    val notes: String? = null
)

@Serializable
data class DeletePrepItemRequest(
    val action: String = "delete_master_prep_item",
    val id: String
)

@Serializable
data class SavePrepRoutineRequest(
    val action: String = "save_prep_routine",
    val id: String? = null,
    @SerialName("day_of_week") val dayOfWeek: String,
    @SerialName("prep_item_id") val prepItemId: String,
    @SerialName("prep_quantity") val prepQuantity: String? = null,
    val notes: String? = null
)

@Serializable
data class DeletePrepRoutineRequest(
    val action: String = "delete_prep_routine",
    val id: String
)

@Serializable
data class SavePlannedPrepItemRequest(
    val action: String = "save_planned_prep_item",
    val id: String? = null,
    val date: String,
    @SerialName("prep_item_id") val prepItemId: String,
    val quantity: String
)

@Serializable
data class DeletePlannedPrepItemRequest(
    val action: String = "delete_planned_prep_item",
    val id: String
)

@Serializable
data class TogglePlannedPrepItemRequest(
    val action: String = "toggle_planned_prep_item",
    val id: String
)

@Serializable
data class OrderItem(
    @SerialName("item_id") val itemId: String,
    val qtySuggested: Double,
    val qtyOrdered: Double,
    val qtyReceived: Double? = null,
    val name: String? = null
)

@Serializable
data class Report(
    val id: String,
    val startDate: String,
    val endDate: String,
    val totalSpent: Double,
    val createdAt: String? = null
)

@Serializable
data class ReportDetail(
    @SerialName("item_id") val itemId: String,
    @SerialName("supplier_id") val supplierId: String,
    @SerialName("report_id") val reportId: String,
    val itemName: String? = null,
    val openingStock: Double,
    val deliveredStock: Double,
    val closingStock: Double,
    val usageAmount: Double,
    val itemSpent: Double
)

@Serializable
data class CreateSupplierRequest(
    val action: String = "create_supplier",
    val name: String,
    val howToOrder: String? = null,
    val orderAddress: String? = null,
    val thresholdOrderPercentage: Int = 0
)

@Serializable
data class EditSupplierRequest(
    val action: String = "edit_supplier",
    @SerialName("supplier_id") val id: String,
    val name: String,
    val howToOrder: String? = null,
    val orderAddress: String? = null,
    val thresholdOrderPercentage: Int = 0
)

@Serializable
data class CreateItemRequest(
    val action: String = "create_item",
    @SerialName("supplier_id") val supplierId: String,
    val name: String,
    val packDescription: String,
    val unitMeasureType: String,
    val packsPerBox: Double,
    val measurePerPack: Double,
    val weeklyUsageRate: Double,
    val price: Double
)

@Serializable
data class EditItemRequest(
    val action: String = "edit_item",
    val id: String,
    @SerialName("supplier_id") val supplierId: String? = null,
    val name: String,
    val packDescription: String,
    val unitMeasureType: String,
    val packsPerBox: Double,
    val measurePerPack: Double,
    val weeklyUsageRate: Double,
    val price: Double
)

@Serializable
data class CreateStockCountRequest(
    val action: String = "create_stock_count",
    @SerialName("supplier_id") val supplierId: String,
    val items: List<StockCountItem>
)

@Serializable
data class EditStockCountRequest(
    val action: String = "edit_stock_count",
    val id: String,
    val items: List<StockCountItem>
)

@Serializable
data class DeleteRequest(
    val action: String,
    val id: String
)

@Serializable
data class CreateOrderRequest(
    val action: String = "create_order",
    @SerialName("supplier_id") val supplierId: String,
    val daysCalculated: Int,
    val status: String,
    @SerialName("receiveImageUrl") val receiveImageUrl: String? = null,
    @SerialName("isActionMissing") val isActionMissing: Int = 0,
    val images: List<String>? = null,
    val items: List<OrderItem>
)

@Serializable
data class EditOrderRequest(
    val action: String = "edit_order",
    val id: String,
    val status: String,
    val receivedAt: String? = null,
    @SerialName("receiveImageUrl") val receiveImageUrl: String? = null,
    @SerialName("isActionMissing") val isActionMissing: Int = 0,
    val images: List<String>? = null,
    val items: List<OrderItem>
)

@Serializable
data class GenerateReportRequest(
    val action: String = "generate_report",
    @SerialName("start_date") val startDate: String,
    @SerialName("end_date") val endDate: String
)

@Serializable
data class ProfileItem(
    val type: String,
    val data: String? = null
)

@Serializable
data class SaveProfileRequest(
    val action: String = "save_profile",
    val items: List<ProfileItem>
)

@Serializable
data class AppResponse(
    val message: String? = null,
    val id: String? = null,
    val error: String? = null
)

@Serializable
data class Service(
    val id: String,
    val title: String,
    val description: String? = null,
    @SerialName("service_date_time") val serviceDateTime: String,
    @SerialName("is_done") val isDone: Int = 0,
    @SerialName("completion_notes") val completionNotes: String? = null,
    @SerialName("outstanding_notes") val outstandingNotes: String? = null,
    @SerialName("completed_date") val completedDate: String? = null,
    @SerialName("is_deleted") val isDeleted: Int = 0,
    val images: List<String>? = emptyList()
)

@Serializable
data class SaveServiceRequest(
    val action: String = "save_service",
    val id: String? = null,
    val title: String,
    val description: String? = null,
    @SerialName("service_date_time") val serviceDateTime: String,
    @SerialName("is_done") val isDone: Int = 0,
    @SerialName("completion_notes") val completionNotes: String? = null,
    @SerialName("outstanding_notes") val outstandingNotes: String? = null,
    @SerialName("completed_date") val completedDate: String? = null,
    @SerialName("is_deleted") val isDeleted: Int = 0,
    val images: List<String>? = null
)

@Serializable
data class DeleteServiceRequest(
    val action: String = "delete_service",
    val id: String
)

@Serializable
data class ToolRequest(
    val id: String,
    val title: String,
    val description: String? = null,
    @SerialName("deadline_date") val deadlineDate: String,
    @SerialName("tool_name") val toolName: String,
    @SerialName("created_at") val createdAt: String? = null,
    @SerialName("completed_date") val completedDate: String? = null,
    @SerialName("is_deleted") val isDeleted: Int = 0,
    val images: List<String>? = emptyList()
)

@Serializable
data class SaveToolRequest(
    val action: String = "save_tool",
    val id: String? = null,
    val title: String,
    val description: String? = null,
    @SerialName("deadline_date") val deadlineDate: String,
    @SerialName("tool_name") val toolName: String,
    @SerialName("completed_date") val completedDate: String? = null,
    @SerialName("is_deleted") val isDeleted: Int = 0,
    val images: List<String>? = null
)

@Serializable
data class DeleteToolRequest(
    val action: String = "delete_tool",
    val id: String
)

@Serializable
data class TrainingCategory(
    val id: String,
    @SerialName("category_name") val categoryName: String,
    @SerialName("sub_category_name") val subCategoryName: String? = null
)

@Serializable
data class TrainingItem(
    val id: String,
    val title: String,
    val description: String? = null,
    @SerialName("category_id") val categoryId: String,
    @SerialName("created_at") val createdAt: String? = null,
    val images: List<String>? = emptyList(),
    @SerialName("category_name") val categoryName: String? = null,
    @SerialName("sub_category_name") val subCategoryName: String? = null
)

@Serializable
data class SaveTrainingCategoryRequest(
    val action: String = "save_training_category",
    val id: String? = null,
    @SerialName("category_name") val categoryName: String,
    @SerialName("sub_category_name") val subCategoryName: String? = null
)

@Serializable
data class DeleteTrainingCategoryRequest(
    val action: String = "delete_training_category",
    val id: String
)

@Serializable
data class SaveTrainingItemRequest(
    val action: String = "save_training_item",
    val id: String? = null,
    val title: String,
    val description: String? = null,
    @SerialName("category_id") val categoryId: String,
    val images: List<String>? = null
)

@Serializable
data class DeleteTrainingItemRequest(
    val action: String = "delete_training_item",
    val id: String
)

@Serializable
data class FileFolder(
    val id: String,
    @SerialName("folder_name") val folderName: String,
    @SerialName("sub_folder_name") val subFolderName: String? = null
)

@Serializable
data class FileItem(
    val id: String,
    val title: String,
    val description: String? = null,
    @SerialName("folder_id") val folderId: String,
    @SerialName("created_at") val createdAt: String? = null,
    val files: List<String>? = emptyList(),
    @SerialName("folder_name") val folderName: String? = null,
    @SerialName("sub_folder_name") val subFolderName: String? = null
)

@Serializable
data class SaveFileFolderRequest(
    val action: String = "save_file_folder",
    val id: String? = null,
    @SerialName("folder_name") val folderName: String,
    @SerialName("sub_folder_name") val subFolderName: String? = null
)

@Serializable
data class DeleteFileFolderRequest(
    val action: String = "delete_file_folder",
    val id: String
)

@Serializable
data class SaveFileItemRequest(
    val action: String = "save_file_item",
    val id: String? = null,
    val title: String,
    val description: String? = null,
    @SerialName("folder_id") val folderId: String,
    val files: List<String>? = null
)

@Serializable
data class DeleteFileItemRequest(
    val action: String = "delete_file_item",
    val id: String
)

@Serializable
data class Delivery(
    val id: String,
    val date: String,
    val title: String,
    val platform: String? = null,
    @SerialName("image_url") val imageUrl: String = "",
    @SerialName("createdAt") val createdAt: String? = null
)

@Serializable
data class VerifyReceiptRequest(
    val action: String = "verify_receipt",
    @SerialName("imageUrl") val imageUrl: String
)

@Serializable
data class ProcessSilentDeliveryRequest(
    val action: String = "process_silent_delivery",
    @SerialName("imageUrl") val imageUrl: String
)

@Serializable
data class ReceiptVerificationResult(
    @SerialName("is_readable") val isReadable: Boolean = false,
    val platform: String? = null,
    @SerialName("order_id") val orderId: String? = null,
    @SerialName("error_message") val errorMessage: String? = null,
    val error: String? = null
)

@Serializable
data class SaveDeliveryRequest(
    val action: String = "save_delivery",
    val date: String,
    val title: String? = null,
    val platform: String? = null,
    @SerialName("imageUrl") val imageUrl: String? = null,
    @SerialName("image_base64") val imageBase64: String? = null
)

@Serializable
data class DeleteDeliveryRequest(
    val action: String = "delete_delivery",
    val id: String
)

fun calculateItemCost(item: Item?, quantity: Double): Double {
    if (item == null) return 0.0
    val unit = item.unitMeasureType.lowercase().trim()
    return if ((unit == "kgp" || unit == "kgs") && item.measurePerPack > 0) {
        (quantity / item.measurePerPack) * item.price
    } else {
        quantity * item.price
    }
}
